import {
  Authenticator,
  Image,
  Text,
  View,
  CheckboxField,
  AuthenticatorProps,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { PropsWithChildren, useEffect, useState, useRef } from "react";

import { getCurrentUser, confirmSignUp } from "aws-amplify/auth";
import { gql, useMutation } from "@apollo/client";
import PageLoader from "../PageLoader";
import StaticHeader from "../../Product/Layout/Header/StaticHeader";
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from "../../../routes/wixRoutes";
import { useMeta } from "../../../contexts/MetaContext";

import styles from "./claimable-authenticator.module.scss";

type LoginMechanisms = Parameters<typeof Authenticator>[0]["loginMechanisms"];

const AuthenticatorHeader = () => (
  <View className={styles.authHeader}>
    <Image alt="Claimable" src="/images/logo/logo_black_large.svg" />
  </View>
);

const Footer = () => (
  <Text variation="tertiary">{`© ${new Date().getFullYear()} All rights reserved.`}</Text>
);

const SignUpAgreeToTermsAndPrivacyPolicy = () => (
  <>
    <Authenticator.SignUp.FormFields />
    <CheckboxField
      name="custom:agreeToTerms"
      value="agree"
      isRequired
      label={
        <>
          I agree to the{" "}
          <a
            href={TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href={PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Privacy Policy
          </a>
        </>
      }
    />
  </>
);

const ForcePasswordAgreeToTermsAndPrivacyPolicy = () => (
  <>
    <Authenticator.ForceNewPassword.FormFields />
    <CheckboxField
      name="custom:agreeToTerms"
      value="agree"
      isRequired
      label={
        <>
          I agree to the{" "}
          <a
            href={TERMS_OF_SERVICE}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href={PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            Privacy Policy
          </a>
        </>
      }
    />
  </>
);

const SignUpHeader = () => (
  <h1 className={styles.signInHeader}>Create account</h1>
);

const ConfirmSignUpHeader = () => {
  const { username } = useAuthenticator((context) => [context.username]);

  return (
    <View className={styles.confirmSignUpHeader}>
      <h4>We Emailed You</h4>
      <Text>
        Your code is on the way. To log in, enter the code we emailed to{" "}
        <strong>{username}</strong>. It may take a minute to arrive.
      </Text>
      <Text className={styles.spamNotice}>
        {"Don't see it? Check your spam, junk, or promotions folders."}
      </Text>
    </View>
  );
};

const ConfirmResetPasswordHeader = () => {
  const { username } = useAuthenticator((context) => [context.username]);

  return (
    <View className={styles.confirmResetPasswordHeader}>
      <h4>We Emailed You</h4>
      <Text>
        Your code is on the way. To reset your password, enter the code we
        emailed to <strong>{username}</strong>. It may take a minute to arrive.
      </Text>
      <Text className={styles.spamNotice}>
        {"Don't see it? Check your spam, junk, or promotions folders."}
      </Text>
    </View>
  );
};

const LoginError = ({ errorMessage }: { errorMessage: string }) => (
  <div className="page_container">
    <div className={styles.claimableAuthenticator}>
      <StaticHeader />
      <div className="max-w-xl mx-auto">
        <p className={styles.loginErrorMessage}>
          {errorMessage}
          <a className="mailto" href="mailto://support@getclaimable.com">
            support@getclaimable.com
          </a>
          {`.`}
        </p>
      </div>
    </div>
  </div>
);

export const SETUP_NEW_USER_WITH_EXISTING_REFERRALS = gql(`
  mutation SetupNewUserWithExistingReferrals($userId: ID!) {
    setupNewUserWithExistingReferrals(userId: $userId) {
      appealId
    }
  }
`);

type ClaimableAuthenticatorProps = {
  hideSignUp?: boolean;
  defaultTab?: "signUp" | "signIn";
  formFields?: AuthenticatorProps["formFields"];
  onLoginComplete?: () => void;
};

const ClaimableAuthenticator = ({
  children,
  hideSignUp = false,
  defaultTab = "signUp",
  formFields,
  onLoginComplete,
}: PropsWithChildren<ClaimableAuthenticatorProps>) => {
  const loginMechanisms: LoginMechanisms = ["email"];

  const { authStatus, toSignIn } = useAuthenticator();
  const { hasUserAuthenticated, setHasUserAuthenticated } = useMeta();
  const [
    attachNewUserToExistingAppeals,
    { loading: mutationRunning, error: mutationError },
  ] = useMutation(SETUP_NEW_USER_WITH_EXISTING_REFERRALS);

  const [shouldRedirectToSignIn, setShouldRedirectToSignIn] = useState(false);
  const [showAccountVerifiedMessage, setShowAccountVerifiedMessage] =
    useState(false);
  const sessionExpiredRef = useRef<{
    setShouldRedirect: (redirect: boolean) => void;
    setShowMessage: (show: boolean) => void;
  }>({
    setShouldRedirect: setShouldRedirectToSignIn,
    setShowMessage: setShowAccountVerifiedMessage,
  });

  const SignInHeader = () => (
    <>
      <h1 className={styles.signInHeader}>Sign in</h1>
      {showAccountVerifiedMessage && (
        <div className={styles.accountVerifiedMessage}>
          Your account has been verified! Please sign in to continue.
        </div>
      )}
    </>
  );

  const themeComponents = {
    Header: AuthenticatorHeader,
    Footer,
    SignUp: {
      FormFields: SignUpAgreeToTermsAndPrivacyPolicy,
      Header: SignUpHeader,
    },
    ForceNewPassword: {
      FormFields: ForcePasswordAgreeToTermsAndPrivacyPolicy,
    },
    SignIn: {
      Header: SignInHeader,
    },
    ConfirmSignUp: {
      Header: ConfirmSignUpHeader,
    },
    ConfirmResetPassword: {
      Header: ConfirmResetPasswordHeader,
    },
  };

  useEffect(() => {
    if (shouldRedirectToSignIn) {
      toSignIn();
      setShouldRedirectToSignIn(false);
    }
  }, [shouldRedirectToSignIn, toSignIn]);

  useEffect(() => {
    async function asyncMethod() {
      if (authStatus === "authenticated" && !hasUserAuthenticated) {
        const user = await getCurrentUser();
        await attachNewUserToExistingAppeals({
          variables: {
            userId: user.userId,
          },
        });
        setHasUserAuthenticated(true);

        if (onLoginComplete) {
          onLoginComplete();
        }
      }
    }

    asyncMethod();
  }, [authStatus]);

  if (mutationError) {
    return (
      <LoginError
        errorMessage={`Failed to complete setting up your account. We're looking into it. If you don't hear from us, please use the link below to contact us with details: `}
      />
    );
  }

  if (mutationRunning) {
    return (
      <div className={styles.claimableAuthenticator}>
        <StaticHeader />
        <PageLoader />
      </div>
    );
  }

  const services = {
    async handleConfirmSignUp(input: {
      username: string;
      confirmationCode: string;
    }) {
      const result = await confirmSignUp({
        username: input.username,
        confirmationCode: input.confirmationCode,
      });

      if (result.nextStep.signUpStep === "DONE") {
        sessionExpiredRef.current.setShouldRedirect(true);
        sessionExpiredRef.current.setShowMessage(true);
      }

      return result;
    },
  };

  return (
    <div className={styles.claimableAuthenticator}>
      <Authenticator
        loginMechanisms={loginMechanisms}
        components={themeComponents}
        hideSignUp={hideSignUp}
        initialState={defaultTab}
        formFields={formFields}
        services={services}
      >
        {children}
      </Authenticator>
    </div>
  );
};

export default ClaimableAuthenticator;
